import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../services/api';
import { useApp } from '../context/AppContext';
import { Badge } from './Badge';
import { AuthorizedAction } from './AuthorizedAction';
import { parseApiError } from '../utils/apiError';

const STATUS_VARIANT = {
  unlinked: 'inactive',
  invited: 'onboarding',
  pending_claim: 'onboarding',
  linked: 'active',
  suspended: 'offboarding',
  revoked: 'inactive',
};

function displayStatus(link) {
  if (!link) return 'unlinked';
  return link.status || 'unlinked';
}

export function EmployeeIdentityLinkPanel({ employee, onUpdated }) {
  const { t, canEdit, canAdmin } = useApp();
  const [link, setLink] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [linkFlowUserId, setLinkFlowUserId] = useState('');
  const [confirmLinkOpen, setConfirmLinkOpen] = useState(false);

  const load = useCallback(() => {
    if (!employee?.id) return;
    setLoading(true);
    setError(null);
    api
      .get(`/api/identity-links/by-employee/${employee.id}`)
      .then((r) => setLink(r.data || null))
      .catch(() => setLink(null))
      .finally(() => setLoading(false));
  }, [employee?.id]);

  useEffect(() => {
    load();
  }, [load]);

  async function runAction(fn) {
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      await fn();
      setSuccess(t('identityLink.actionOk'));
      load();
      onUpdated?.();
    } catch (err) {
      const { message, code } = parseApiError(err);
      setError(message || code || t('identityLink.actionError'));
    } finally {
      setBusy(false);
    }
  }

  const status = displayStatus(link);
  const permEdit = canEdit('employees');
  const permAdmin = canAdmin('employees');

  return (
    <section className="panel panel--elevated identity-link-panel" aria-labelledby={`identity-link-${employee.id}`}>
      <h3 className="panel__title" id={`identity-link-${employee.id}`}>
        {t('identityLink.title')}
      </h3>
      {loading ? (
        <p className="panel__muted text-sm">{t('common.loading')}</p>
      ) : (
        <>
          <div className="identity-link-panel__status">
            <span className="text-sm panel__muted">{t('identityLink.statusLabel')}</span>
            <Badge variant={STATUS_VARIANT[status] || 'inactive'}>{t(`identityLink.status.${status}`)}</Badge>
          </div>
          {link?.suspended_reason && (
            <p className="text-sm panel__muted">{link.suspended_reason}</p>
          )}
          {error && (
            <p className="identity-link-panel__error text-sm" role="alert">
              {error}
            </p>
          )}
          {success && (
            <p className="identity-link-panel__success text-sm" role="status">
              {success}
            </p>
          )}
          <div className="identity-link-panel__actions">
            {(status === 'unlinked' || !link) && (
              <AuthorizedAction
                module="employees"
                action="edit"
                className="btn btn--sm btn--primary"
                disabled={busy || !permEdit}
                onClick={() =>
                  runAction(() => api.post('/api/identity-links/invite', { employee_id: employee.id, email: employee.email }))
                }
              >
                {t('identityLink.invite')}
              </AuthorizedAction>
            )}
            {status === 'invited' && link && (
              <AuthorizedAction
                module="employees"
                action="edit"
                className="btn btn--sm btn--secondary"
                disabled={busy || !permEdit}
                onClick={() => runAction(() => api.post(`/api/identity-links/${link.id}/resync`))}
              >
                {t('identityLink.resend')}
              </AuthorizedAction>
            )}
            {(status === 'unlinked' || status === 'pending_claim' || status === 'invited') && (
              <AuthorizedAction
                module="employees"
                action="edit"
                className="btn btn--sm btn--secondary"
                disabled={busy || !permEdit}
                onClick={() => setConfirmLinkOpen(true)}
              >
                {t('identityLink.linkExisting')}
              </AuthorizedAction>
            )}
            {status === 'linked' && link && (
              <>
                <AuthorizedAction
                  module="employees"
                  action="admin"
                  className="btn btn--sm btn--secondary"
                  disabled={busy || !permAdmin}
                  onClick={() =>
                    runAction(() =>
                      api.post(`/api/identity-links/${link.id}/suspend`, null, { params: { reason: 'admin_suspend' } })
                    )
                  }
                >
                  {t('identityLink.suspend')}
                </AuthorizedAction>
                <AuthorizedAction
                  module="employees"
                  action="admin"
                  className="btn btn--sm btn--danger"
                  disabled={busy || !permAdmin}
                  onClick={() =>
                    runAction(() =>
                      api.post(`/api/identity-links/${link.id}/revoke`, null, { params: { reason: 'admin_revoke' } })
                    )
                  }
                >
                  {t('identityLink.revoke')}
                </AuthorizedAction>
              </>
            )}
            {status === 'suspended' && link && (
              <AuthorizedAction
                module="employees"
                action="edit"
                className="btn btn--sm btn--secondary"
                disabled={busy || !permEdit}
                onClick={() => runAction(() => api.post(`/api/identity-links/${link.id}/resync`))}
              >
                {t('identityLink.retrySync')}
              </AuthorizedAction>
            )}
          </div>
        </>
      )}

      {confirmLinkOpen && (
        <div className="modal-backdrop" role="dialog" aria-modal aria-labelledby="link-confirm-title">
          <div className="modal">
            <h2 className="modal__title" id="link-confirm-title">
              {t('identityLink.confirmTitle')}
            </h2>
            <p className="text-sm panel__muted">{t('identityLink.confirmLead')}</p>
            <label className="field field--full">
              <span>{t('identityLink.flowUserId')}</span>
              <input
                className="input"
                value={linkFlowUserId}
                onChange={(e) => setLinkFlowUserId(e.target.value)}
                autoComplete="off"
              />
            </label>
            <div className="modal__actions">
              <button type="button" className="btn btn--ghost" onClick={() => setConfirmLinkOpen(false)}>
                {t('employees.cancel')}
              </button>
              <button
                type="button"
                className="btn btn--primary"
                disabled={!linkFlowUserId.trim() || busy}
                onClick={() =>
                  runAction(async () => {
                    const body = {
                      employee_id: employee.id,
                      flow_user_id: linkFlowUserId.trim(),
                      status: 'linked',
                      link_method: 'manual',
                    };
                    await api.post('/api/identity-links', body);
                    setConfirmLinkOpen(false);
                  })
                }
              >
                {t('identityLink.confirmLink')}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
